import React from 'react';
import { Link } from 'react-router-dom';

import { usePaperDocument } from '../hooks/usePaperDocument';
import './paper.css';

const REPO = 'https://github.com/TheoTime01/Human-Robot-Interaction-HRI-Project';
const VIDEO = 'https://youtu.be/tRi2CV4AP5A';
const EMBED = 'https://www.youtube-nocookie.com/embed/tRi2CV4AP5A';
const VIDEO_S1 = 'https://youtu.be/tRi2CV4AP5A?t=3';
const VIDEO_S2 = 'https://youtu.be/tRi2CV4AP5A?t=63';

/* Display equations are written as MathML so that they render natively in the
 * document font instead of pulling in a formula-typesetting library. React
 * renders MathML tags, but going through innerHTML keeps the markup verbatim
 * and keeps this file readable. */
const Equation = ({ tag, math }) => (
  <div className="eq">
    <div className="eq-body" dangerouslySetInnerHTML={{ __html: math }} />
    <div className="eq-tag">({tag})</div>
  </div>
);

const EQ_DIVIDER = `
<math display="block">
  <msub><mi>V</mi><mi>out</mi></msub><mo>=</mo>
  <mfrac>
    <msub><mi>R</mi><mn>2</mn></msub>
    <mrow><msub><mi>R</mi><mn>1</mn></msub><mo>+</mo><msub><mi>R</mi><mn>2</mn></msub></mrow>
  </mfrac>
  <mspace width="0.2em"/>
  <msub><mi>V</mi><mi>cc</mi></msub>
</math>`;

const EQ_AMPERE = `
<math display="block">
  <mo>&#x2207;</mo><mo>&#x00D7;</mo><mi mathvariant="bold">B</mi>
  <mo>=</mo>
  <msub><mi>&#x3BC;</mi><mn>0</mn></msub><mi mathvariant="bold">J</mi>
  <mspace width="1.5em"/><mo>&#x27F9;</mo><mspace width="1.5em"/>
  <mo>&#x222E;</mo><mi mathvariant="bold">B</mi><mo>&#x22C5;</mo>
  <mi>d</mi><mi mathvariant="bold">&#x2113;</mi>
  <mo>=</mo>
  <msub><mi>&#x3BC;</mi><mn>0</mn></msub><mi>I</mi>
</math>`;

const EQ_FARADAY = `
<math display="block">
  <mo>&#x2207;</mo><mo>&#x00D7;</mo><mi mathvariant="bold">E</mi>
  <mo>=</mo><mo>&#x2212;</mo>
  <mfrac>
    <mrow><mo>&#x2202;</mo><mi mathvariant="bold">B</mi></mrow>
    <mrow><mo>&#x2202;</mo><mi>t</mi></mrow>
  </mfrac>
  <mspace width="1.5em"/><mo>&#x27F9;</mo><mspace width="1.5em"/>
  <mi>e</mi><mo>=</mo><mo>&#x2212;</mo>
  <mfrac>
    <mrow><mi>d</mi><msub><mi>&#x3A6;</mi><mi>B</mi></msub></mrow>
    <mrow><mi>d</mi><mi>t</mi></mrow>
  </mfrac>
</math>`;

const EQ_RESISTOR = `
<math display="block">
  <mi>R</mi><mo>=</mo>
  <mrow><mo stretchy="true">(</mo>
    <mn>10</mn><msub><mi>d</mi><mn>1</mn></msub>
    <mo>+</mo>
    <msub><mi>d</mi><mn>2</mn></msub>
  <mo stretchy="true">)</mo></mrow>
  <mo>&#x22C5;</mo>
  <msup><mn>10</mn><mi>m</mi></msup>
  <mspace width="1em"/>
  <mo>&#x00B1;</mo><mspace width="0.2em"/><mi>t</mi><mo>%</mo>
  <mo>,</mo><mspace width="1.5em"/>
  <msub><mi>d</mi><mi>i</mi></msub><mo>&#x2208;</mo>
  <mrow><mo>[</mo><mn>0</mn><mo>,</mo><mn>9</mn><mo>]</mo></mrow>
  <mo>,</mo><mspace width="0.6em"/>
  <mi>m</mi><mo>&#x2208;</mo>
  <mrow><mo>[</mo><mn>0</mn><mo>,</mo><mn>9</mn><mo>]</mo></mrow>
</math>`;

/* Sequence diagram for the one interaction branch that exists only in the
 * ethical build (§4.3, §7.2). Drawn inline rather than exported from a tool so
 * that it stays editable alongside the prose; it is placed on a white plate by
 * `fig-plate` so the black strokes survive dark mode. */
const LANES = [
  { x: 56, top: 'Student', bottom: '' },
  { x: 180, top: 'ALDialog', bottom: 'simple_en.top' },
  { x: 304, top: 'ALMemory', bottom: '' },
  { x: 428, top: 'Tablet view', bottom: 'sample.js' },
  { x: 552, top: 'facts service', bottom: 'application.py' }
];

const MESSAGES = [
  { from: 3, to: 2, y: 118, label: "raiseEvent('SimpleWeb/Start')" },
  { from: 2, to: 1, y: 152, label: 'e:SimpleWeb/Start' },
  { from: 1, to: 0, y: 186, label: '\u201cHow can I help you ?\u201d' },
  { from: 0, to: 1, y: 220, label: '\u201cI am bored\u201d' },
  { from: 1, to: 0, y: 254, label: '\u201c\u2026Can I tell you something ?\u201d' },
  { from: 0, to: 1, y: 288, label: '\u201cyes\u201d' },
  { from: 1, to: 4, y: 322, label: '^call(facts.get_fact())' },
  { from: 4, to: 1, y: 380, label: 'return string \u2192 $1', dashed: true },
  { from: 1, to: 0, y: 414, label: 'c3:(_*) $1 \u2014 robot speaks it' },
  { from: 1, to: 2, y: 448, label: '$\u2026/Page/Empty=1' },
  { from: 2, to: 3, y: 482, label: 'signal \u2192 show #page_vide' }
];

const SequenceDiagram = () => (
  <svg
    viewBox="0 0 640 520"
    role="img"
    aria-label="Sequence diagram of the ethical build's boredom branch, from the tablet start event through the dialogue engine and the facts service to the spoken answer"
    style={{ width: '100%', height: 'auto', fontFamily: 'inherit' }}
  >
    <defs>
      <marker id="hri-arrow" viewBox="0 0 10 10" refX="9" refY="5"
              markerWidth="7" markerHeight="7" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#111" />
      </marker>
    </defs>

    {LANES.map((lane) => (
      <g key={lane.x}>
        <rect x={lane.x - 59} y="18" width="118" height="46" rx="3"
              fill="#fff" stroke="#111" strokeWidth="1.2" />
        <text x={lane.x} y={lane.bottom ? 39 : 46} textAnchor="middle"
              fontSize="12.5" fill="#111">{lane.top}</text>
        {lane.bottom && (
          <text x={lane.x} y="55" textAnchor="middle" fontSize="10.5" fill="#444">
            {lane.bottom}
          </text>
        )}
        <line x1={lane.x} y1="64" x2={lane.x} y2="505"
              stroke="#111" strokeWidth="0.8" strokeDasharray="4 4" />
      </g>
    ))}

    {MESSAGES.map((m) => {
      const x1 = LANES[m.from].x;
      const x2 = LANES[m.to].x;
      return (
        <g key={m.y}>
          <line x1={x1} y1={m.y} x2={x2} y2={m.y}
                stroke="#111" strokeWidth="1.2" markerEnd="url(#hri-arrow)"
                strokeDasharray={m.dashed ? '5 4' : undefined} />
          <text x={(x1 + x2) / 2} y={m.y - 7} textAnchor="middle"
                fontSize="12.5" fill="#111"
                stroke="#fff" strokeWidth="3.5" paintOrder="stroke">
            {m.label}
          </text>
        </g>
      );
    })}

    {/* The one hop that leaves the robot: a blocking HTTPS GET on the qi
      * service thread (§6.2, finding F4). */}
    <path d="M 552 336 L 600 336 L 600 364 L 552 364"
          fill="none" stroke="#111" strokeWidth="1.2"
          strokeDasharray="5 4" markerEnd="url(#hri-arrow)" />
    <text x="638" y="331" textAnchor="end" fontSize="11.5" fill="#111">
      HTTPS GET
    </text>
    <text x="638" y="378" textAnchor="end" fontSize="11.5" fill="#111">
      api-ninjas.com
    </text>
  </svg>
);

const HriEthicsProject = () => {
  const { theme, toggleTheme } = usePaperDocument(
    'HRI ethics on Pepper: one dialogue file apart | Théotime Perrichet'
  );

  return (
    <article className="paper-doc">
      <div className="doc-bar">
        <Link to="/">&larr; Théotime Perrichet</Link>
        <button type="button" onClick={toggleTheme}>
          {theme === 'dark' ? ': Light :' : ': Dark :'}
        </button>
      </div>

      <h1>
        One dialogue file apart: an ethical and a non-ethical Pepper tutor
        built from a single source tree
      </h1>

      <p className="author">
        Théotime Perrichet &nbsp;&middot;&nbsp; Arnaud Sibenaler
      </p>
      <p className="doc-affiliation">
        Human-robot interaction project, M.Eng. Robotics, CPE Lyon
      </p>
      <p className="doc-dateline">Lyon, France · April 2023</p>

      <ul className="doc-links">
        <li><a href={REPO}>Source code</a></li>
        <li><a href={VIDEO}>Demonstration video</a></li>
      </ul>

      <div className="abstract">
        <h2>Abstract</h2>
        <p>
          This report documents a paired-scenario human-robot interaction study
          apparatus built on a SoftBank Pepper running NAOqi&nbsp;2.5.5.5. The
          robot acts as an electronics tutor: it answers spoken requests, drives
          its own chest tablet through a web view, calls a locally registered
          NAOqi service, and reaches an external HTTP web service over the
          internet. Two conditions are provided, one in which the robot
          behaves ethically toward the student and one in which it does not,
          and the engineering claim of this write-up is a measurement of that
          manipulation rather than a description of it. Across roughly 620 lines
          of hand-written source, the two builds differ in exactly one file and
          exactly two hunks: seven lines removed, five added, in a declarative{' '}
          <code>qichat</code> dialogue script. Every Python, HTML, JavaScript,
          CSS and image file is byte-identical between the conditions,
          confirmed by checksum. That confinement is the property that makes the
          comparison mean anything, and we take it as the object of study.
          We then show that the confinement is nonetheless <em>leaky</em>: the
          five-line diff silently deletes the only call site of the external
          web service, so the two conditions differ not merely in politeness but
          in system composition, network dependency and response-latency
          distribution, a confound that would be invisible to anyone reading
          the transcripts rather than the diff. Twenty-one findings are
          reported from a read of the source, including a stylesheet that is
          never loaded, a dead tablet page referenced six times, a Python&nbsp;2
          decode that raises on any non-ASCII response from the web service, and
          a rule guarding the study&rsquo;s most ethically loaded utterance
          behind a phrase no speech recogniser is likely to produce.
        </p>
        <p className="keywords">
          <strong>Keywords:</strong> human-robot interaction · roboethics ·
          Pepper · NAOqi · qichat dialogue · experimental confounds ·
          service architecture · code review
        </p>
      </div>

      <nav aria-label="Table of contents">
        <ol>
          <li><a href="#s1">Introduction</a></li>
          <li><a href="#s2">Platform and interfaces</a></li>
          <li><a href="#s3">Software architecture</a></li>
          <li><a href="#s4">The dialogue layer</a></li>
          <li><a href="#s5">The tablet application</a></li>
          <li><a href="#s6">The NAOqi service and the external web service</a></li>
          <li><a href="#s7">The manipulation, measured</a></li>
          <li><a href="#s8">What the two conditions do and do not isolate</a></li>
          <li><a href="#s9">Defect and robustness analysis</a></li>
          <li><a href="#s10">Results</a></li>
          <li><a href="#s11">Limitations and further work</a></li>
          <li><a href="#s12">Reproducing the system</a></li>
        </ol>
      </nav>

      <h2 id="s1">1. Introduction</h2>
      <p>
        Ethics in human-robot interaction is usually discussed as a property of
        a robot. It is more useful to an engineer as a property of a{' '}
        <em>diff</em>. A social robot behaves well or badly toward the person in
        front of it because somebody wrote one string rather than another, put a
        consent question in front of an action or left it out, provided a path
        for refusal or provided none. Those are edits. They live somewhere
        specific in a source tree, they have a size, and they can be reviewed
        like any other change.
      </p>
      <p>
        The brief for this project asked for two scenarios on a SoftBank Pepper
        (ideally two variations of the same scenario), of which one should
        exhibit ethical behaviour and the other should not, subject to four
        technical constraints: use the tablet, use the dialogue/qichat module,
        register a service in the NAOqi sense and interface it with the
        dialogue, and connect to a web service. The application domain chosen
        was electronics tutoring: the robot explains the voltage divider,
        Ampère&rsquo;s law and Faraday&rsquo;s law, and drives an interactive
        resistor colour-code calculator on its chest tablet.
      </p>
      <p>
        This document reconstructs that system as built and then treats the
        pair of conditions as the artefact under review. §2 to §6 describe the
        platform, the process graph, the dialogue script, the tablet view and
        the service layer. §7 measures the manipulation exactly, naming which
        files differ, by how much, and in which direction, and §8 asks the
        question
        that measurement makes possible: given that the two builds share a
        byte-identical executable substrate, what is actually being varied, and
        what is being varied by accident. §9 is an ordinary code review of the
        substrate itself, because an apparatus whose failure modes are unknown
        cannot support a claim about human perception. Every figure quoted below
        is read from the repository <a href="#r9">[9]</a> at commit{' '}
        <code>e56b93b</code>; derived quantities are marked as derived, and no
        new experiments were run for this write-up.
      </p>
      <p>
        One thing this project is not, and the report says so in §10 rather than
        implying otherwise throughout: it is not a study. No participants were
        run, no perception or trust instrument was administered, and no
        interaction was logged. What was built is the apparatus, and the
        apparatus is where the interesting engineering is.
      </p>

      <figure>
        <div className="video-frame">
          <iframe
            src={EMBED}
            title="Pepper HRI ethics: demonstration video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
          />
        </div>
        <figcaption>
          Demonstration video. The ethical condition runs from{' '}
          <a href={VIDEO_S1}>0:03</a> and the non-ethical condition from{' '}
          <a href={VIDEO_S2}>1:03</a>; both timestamps are the ones given in the
          project README <a href="#r9">[9]</a>. If the embed does not load, the
          video is on <a href={VIDEO}>YouTube</a>.
        </figcaption>
      </figure>

      <h2 id="s2">2. Platform and interfaces</h2>

      <h3>2.1 The robot</h3>
      <p>
        Pepper <a href="#r1">[1]</a> is a wheeled humanoid with a chest-mounted
        Android tablet, built around the NAOqi middleware. For this project the
        platform matters in three respects and no others. It has a microphone
        array and a speech recogniser, so spoken input is available but
        unreliable. It has a text-to-speech engine with a fixed, neutral voice,
        so an utterance is delivered the same way regardless of what it says,
        a fact §8 returns to, because it means the manipulation here is purely
        lexical. And it has a screen large enough to carry content, which turns
        the robot from a speaker into a tutor with a blackboard.
      </p>
      <p>
        The toolchain is pinned by the paths in the project README: the
        Python&nbsp;2.7 NAOqi SDK <code>pynaoqi-python2.7-2.5.5.5-linux64</code>{' '}
        and <code>choregraphe-suite-2.5.5.5</code>. NAOqi&nbsp;2.5 is a
        Python&nbsp;2 world, and every consequence of that in §9 follows from a
        constraint of the platform rather than a choice of the authors.
      </p>

      <h3>2.2 The four interfaces used</h3>
      <p>
        Everything the system does passes through four NAOqi services, reached
        over the qi bus at <code>tcp://&lt;robot&gt;:9559</code>:
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th>Service</th>
              <th>Used for</th>
              <th>Called from</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>ALDialog</code> <a href="#r2">[2]</a></td>
              <td>
                Loads, activates and runs the <code>qichat</code> topic; owns
                all conversational state
              </td>
              <td><code>app.py</code></td>
            </tr>
            <tr>
              <td><code>ALMemory</code> <a href="#r3">[3]</a></td>
              <td>
                Event bus between the dialogue engine and the tablet, in both
                directions
              </td>
              <td>
                <code>simple_en.top</code>, <code>sample.js</code>
              </td>
            </tr>
            <tr>
              <td><code>ALTabletService</code> <a href="#r4">[4]</a></td>
              <td>Loads the packaged web application and shows the web view</td>
              <td><code>app.py</code></td>
            </tr>
            <tr>
              <td><code>ALTextToSpeech</code></td>
              <td>
                Obtained in the constructor; unused on the live code path (§9,
                F19)
              </td>
              <td><code>app.py</code></td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        A fifth service, <code>facts</code>, is not a NAOqi service but one this
        project registers on the same bus (§6). That is the whole interface
        surface: no vision, no motion, no localisation, no touch sensors. The
        robot is used as a conversational agent with a screen, and the design
        space is correspondingly narrow, which is exactly right for a study
        whose independent variable is what the robot says.
      </p>

      <h2 id="s3">3. Software architecture</h2>

      <h3>3.1 Process inventory</h3>
      <p>
        The running system is four processes, of which the project supplies
        three. Nothing is a framework: there is no node graph, no state machine
        object, no controller. The composition is done entirely by the qi bus
        and by ALMemory events.
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th>Process</th>
              <th>Language</th>
              <th>Lines</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>app.py</code></td>
              <td>Python 2.7</td>
              <td className="num">97</td>
              <td>
                Session bootstrap: shows the web view, loads and activates the
                topic, subscribes the dialogue engine, then blocks on{' '}
                <code>raw_input</code> until the operator ends the session
              </td>
            </tr>
            <tr>
              <td><code>application.py</code></td>
              <td>Python 2.7</td>
              <td className="num">39</td>
              <td>
                Registers the <code>facts</code> service on the bus and serves
                one method, <code>get_fact()</code>, backed by an external HTTP
                API
              </td>
            </tr>
            <tr>
              <td>NAOqi (<code>ALDialog</code> et al.)</td>
              <td>n/a</td>
              <td className="num">n/a</td>
              <td>
                Platform. Runs the <code>qichat</code> interpreter, the speech
                recogniser and the text-to-speech engine
              </td>
            </tr>
            <tr>
              <td>Tablet web view</td>
              <td>HTML / JS</td>
              <td className="num">335</td>
              <td>
                <code>index.html</code> (202) plus <code>sample.js</code> (123)
                and <code>resistor.js</code> (110); connects back to the qi bus
                through <code>qimessaging.js</code> <a href="#r5">[5]</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        The declarative half of the system is one file, <code>simple_en.top</code>{' '}
        (31 lines in the ethical build, 28 in the non-ethical one), and it is
        where all the conversational logic lives, including, as §7 shows, the
        entire experimental manipulation.
      </p>

      <h3>3.2 ALMemory as the bus</h3>
      <p>
        The only coupling between the dialogue and the tablet is ALMemory, used
        in both directions and with no shared code at all.
      </p>
      <p>
        Downward, a <code>qichat</code> rule ends with an assignment of the
        form <code>$projet_ihr_sibenaler_perrichet/Page/Resistance=1</code>.
        The dialogue engine raises an ALMemory event under that key; in the
        tablet browser, <code>sample.js</code> holds a subscriber on the same
        key and its callback shows the corresponding <code>&lt;div&gt;</code>.
        The key <em>is</em> the interface, and it is stringly typed on both
        sides, and nothing would catch a rename on one side and not
        the other, which is precisely the class of rot that produced finding
        F2.
      </p>
      <p>
        Upward, the start page raises <code>SimpleWeb/Start</code> when it is
        touched, and the top-level rule is written to fire on either the spoken
        greeting or that event:
      </p>

      <pre><code>{`u:([~hello e:SimpleWeb/Start]) How can I help you ? $…/Page/Empty=1`}</code></pre>

      <p>
        This is a good piece of design and worth naming. Two input modalities,
        speech and touch, are unified at the <em>rule</em> level rather than in
        application code, by putting the event and the concept in the same
        alternation. A student who cannot make himself understood by the
        recogniser can start the interaction by touching the screen, and
        nothing downstream needs to know which happened. It is also the only
        redundancy in the system: every other transition is speech-only, which
        is what makes finding F14 as costly as it is.
      </p>

      <figure>
        <div className="fig-plate">
          <SequenceDiagram />
        </div>
        <figcaption>
          The <em>boredom</em> branch of the ethical build, end to end. The
          exchange starts from a touch on the tablet, runs through the dialogue
          engine, leaves the robot for an external HTTP API, comes back as a
          string bound to <code>$1</code>, is spoken, and only then updates the
          screen. This entire branch, the consent question, the refusal path
          and the only call site of the web service, is what the five-line diff
          of §7 replaces with an insult.
        </figcaption>
      </figure>

      <h3>3.3 The tablet is a slave view</h3>
      <p>
        No state is held in the browser. Every subscriber callback in{' '}
        <code>sample.js</code> renders the complete page set, showing one{' '}
        <code>&lt;div&gt;</code> and hiding the rest, rather than
        toggling from whatever was previously visible. That is the right
        pattern, since it is idempotent, it cannot leave two pages up, and it
        makes every transition legal from every state, and it is worth stating
        plainly because it is open-coded six times rather than written once
        (F18).
      </p>
      <p>
        The consequence is that the conversational state and the visible state
        are the same variable, held in the dialogue engine. The screen cannot
        disagree with the robot. It also cannot correct it: if the recogniser
        mishears and no rule fires, nothing is raised, the page does not change,
        and the student is left looking at a resistor calculator while the
        robot waits for a phrase he has no way of discovering. §9 (F14) returns
        to this.
      </p>

      <h2 id="s4">4. The dialogue layer</h2>

      <h3>4.1 The qichat topic</h3>
      <p>
        <code>ALDialog</code> interprets a <code>qichat</code> script
        <a href="#r2">[2]</a>: a tree of rules in which <code>u:</code> is a
        top-level user rule and <code>u1:</code>, <code>u2:</code>,{' '}
        <code>u3:</code> are subrules reachable only while their parent holds
        the conversational focus. A rule is a trigger pattern, then an output
        utterance, then any side effects, such as an ALMemory assignment or a{' '}
        <code>^call</code> to a bus service. Patterns support alternation{' '}
        <code>[a b]</code>, optional words <code>{'{'}word{'}'}</code>, a
        wildcard <code>*</code>, and a capturing wildcard <code>_*</code> read
        back as <code>$1</code>.
      </p>
      <p>
        The entire ethical topic is 31 lines. It declares one concept and one
        top-level rule, under which sit three branches:
      </p>

      <pre><code>{`concept:(hello) [hello hi pepper]

u:([~hello e:SimpleWeb/Start]) How can I help you ? $…/Page/Empty=1
    u1:(* resistor)      → tablet: resistor calculator
    u1:(* explanation)   → tablet: menu of three demonstrations
        u2:(* voltage divider bridge) / (* Ampere's law) / (* Faraday's law)
    u1:({I am} bored)    → consent question, then the facts service`}</code></pre>

      <p>
        Three observations follow directly from the shape of that tree, and all
        three become findings in §9.
      </p>
      <p>
        First, every branch is a child of the greeting. A student who walks up
        and says &ldquo;resistor&rdquo; matches nothing, because the{' '}
        <code>u1:</code> rules are not reachable until the top rule has fired.
        The interaction has exactly one entry point, and it is undiscoverable
        except by being told (F14).
      </p>
      <p>
        Second, the wildcards are one-sided. <code>(* resistor)</code> matches
        any utterance <em>ending</em> in &ldquo;resistor&rdquo;, so &ldquo;can
        you help me with a resistor&rdquo; matches and &ldquo;I need the
        resistor tool&rdquo; does not. The same asymmetry applies to{' '}
        <code>(* explanation)</code> and to all three demonstration rules.
        Writing <code>(* resistor *)</code> costs two characters and removes
        the failure (F15).
      </p>
      <p>
        Third, there is no fallback anywhere: no catch-all rule at any depth,
        no timeout rule, no reprompt. Every unmatched utterance is silence.
        Combined with §3.3, silence means the student cannot tell whether he was
        misheard, whether the robot is thinking, or whether the phrase was
        simply wrong (F14).
      </p>

      <h3>4.2 Rule triggers as an ASR contract</h3>
      <p>
        A <code>qichat</code> trigger is a literal word sequence, so each rule
        is in effect a contract with the speech recogniser: the participant must
        produce a transcript that matches, and the transcript is what the
        recogniser heard rather than what was said. That makes trigger phrasing
        a robustness parameter, not a cosmetic one, and it is worth testing the
        rules against it.
      </p>
      <p>
        The two design flowcharts committed with the project (Figures 4 and 5)
        are the specification, and they do not agree with the implementation on
        two triggers. The flowcharts label the calculator branch{' '}
        <code>H: {'{'}Resistance{'}'}</code>, while the rule matches{' '}
        <code>resistor</code>; a participant who follows the flowchart, or who
        reads the tablet page title, says the word that does not match. They
        likewise label one demonstration <em>Ampere&rsquo;s theorem</em> where
        the rule requires <em>Ampere&rsquo;s law</em> (F11).
      </p>
      <p>
        The sharpest instance is in the non-ethical topic. The rule that
        produces the study&rsquo;s single most ethically loaded line is:
      </p>

      <pre><code>{`u2:(Why are saying this) because even if you were a robot, you will be useless !`}</code></pre>

      <p>
        The trigger is an exact four-word phrase with no wildcard, and it is
        ungrammatical. A participant asks &ldquo;why are <em>you</em> saying
        this&rdquo;, the recogniser transcribes a grammatical English sentence,
        and the rule does not fire. The insult that the entire non-ethical
        condition is built around is, on a reading of the source, very probably
        unreachable (F12), and because there is no fallback rule, the failure
        presents as the robot saying nothing at all, which is a different
        stimulus again. A single wildcard, <code>(* saying this *)</code>, is
        the whole fix.
      </p>

      <h3>4.3 Calling out of the dialogue</h3>
      <p>
        One rule leaves the dialogue engine. In the ethical build, a student who
        answers <em>yes</em> to the consent question reaches:
      </p>

      <pre><code>{`u2:(yes) $…/Page/Empty=1 ^call(facts.get_fact())
    c3:(_*) $1
        u4:(Thank you) You're welcome ! $…/Page/Start=1`}</code></pre>

      <p>
        <code>^call</code> invokes <code>get_fact</code> on the{' '}
        <code>facts</code> service registered by <code>application.py</code>{' '}
        (§6). The returned string is fed back into the rule&rsquo;s subtree,
        where <code>c3:(_*)</code> captures it whole into <code>$1</code> and
        the engine speaks it. The indirection is worth noticing: the fact is not
        spoken by the caller, it is routed <em>through</em> the dialogue tree as
        if it were an input, which is why a failure inside{' '}
        <code>get_fact</code> does not surface as an error message but as a
        branch that goes quiet (F4).
      </p>
      <p>
        This is the only place in the system where the robot depends on the
        internet, the only place where a call can block, and, as §7 shows, the
        only place that the non-ethical build deletes.
      </p>

      <h2 id="s5">5. The tablet application</h2>

      <h3>5.1 Page set and rendering</h3>
      <p>
        The web application is a single HTML document holding seven sibling{' '}
        <code>&lt;div&gt;</code> elements, one per screen, all hidden at load
        except the start page. Navigation is not navigation: nothing routes,
        nothing fetches, and the browser never leaves the page. The mapping from
        ALMemory key to element is fixed and total:
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th>ALMemory key (suffix)</th>
              <th>Element</th>
              <th>Content</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>Page/Start</code></td>
              <td><code>#page_start</code></td>
              <td>&ldquo;Hello! Click to Start&rdquo;; raises <code>SimpleWeb/Start</code> on touch</td>
            </tr>
            <tr>
              <td><code>Page/Empty</code></td>
              <td><code>#page_vide</code></td>
              <td>Blank; shown while the robot is speaking</td>
            </tr>
            <tr>
              <td><code>Page/Selection</code></td>
              <td><code>#page_selection</code></td>
              <td>The three demonstration titles, as a spoken-command menu</td>
            </tr>
            <tr>
              <td><code>Page/Resistance</code></td>
              <td><code>#page_resistance</code></td>
              <td>Canvas resistor, four colour selectors, computed value</td>
            </tr>
            <tr>
              <td><code>Page/DemoPdt</code></td>
              <td><code>#page_demo_pdt</code></td>
              <td>Voltage divider: schematic plus a plain-text derivation</td>
            </tr>
            <tr>
              <td><code>Page/DemoAmpere</code></td>
              <td><code>#page_demo_ampere</code></td>
              <td>Ampère&rsquo;s law, typeset by MathJax <a href="#r7">[7]</a></td>
            </tr>
            <tr>
              <td><code>Page/DemoFaraday</code></td>
              <td><code>#page_demo_faraday</code></td>
              <td>Faraday&rsquo;s law, typeset by MathJax</td>
            </tr>
            <tr>
              <td>(none)</td>
              <td><code>#page_random_fact</code></td>
              <td>
                Hidden and shown in all six callbacks; <strong>does not exist</strong>{' '}
                in the document (F2)
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        The last row is the cost of the stringly-typed interface of §3.2. jQuery
        returns an empty set for an unknown selector and <code>.hide()</code> on
        an empty set is a silent no-op, so six dead calls survived into the
        demonstration without a single warning. The fact returned by the web
        service is therefore spoken but never displayed, since the tablet sits on
        the blank page throughout, which was very likely not the intent, given
        that every other robot utterance in the system has a screen to go with
        it.
      </p>

      <h3>5.2 The content, and its network dependency</h3>
      <p>
        Two of the three demonstrations are typeset with MathJax loaded from a
        public CDN. The Ampère page carries the differential form and derives
        the integral form through Stokes&rsquo; theorem:
      </p>

      <Equation tag="1" math={EQ_AMPERE} />

      <p>and the Faraday page does the same for the induced electromotive force:</p>

      <Equation tag="2" math={EQ_FARADAY} />

      <p>
        The third does not. The voltage-divider page writes its algebra as plain
        HTML, as <code>Vout = R2 x I = (R2 / (R1 + R2)) x Vcc</code>, rather than
        as markup MathJax will typeset, so the page that is the visual
        centrepiece of the ethical/non-ethical comparison (§7.1) is also the one
        rendered least well:
      </p>

      <Equation tag="3" math={EQ_DIVIDER} />

      <figure>
        <div className="fig-plate fig-narrow">
          <img
            src="/images/hri/voltage-divider.jpg"
            alt="Schematic of a two-resistor voltage divider: Vcc across R1 and R2 in series, Vout taken at their junction"
          />
        </div>
        <figcaption>
          The voltage-divider schematic shown alongside the derivation on{' '}
          <code>#page_demo_pdt</code>. This is the page both conditions display,
          unchanged, while the robot&rsquo;s spoken framing of it is the
          manipulation (§7.1). Reproduced from the project repository{' '}
          <a href="#r9">[9]</a>.
        </figcaption>
      </figure>

      <p>
        The dependency is worth stating in operational terms, because the
        project README already states its symptom: <em>if there is no internet
        connection, it will be impossible to display the equations in
        LaTeX</em>. On a lab network without egress, the two MathJax pages
        degrade to their raw source, so the student sees{' '}
        <code>{'$$\\nabla \\times \\mathbf{B} = \\mu_0 \\mathbf{J}$$'}</code>,
        and the degradation hits the <em>teaching content</em>, not the
        decoration. jQuery is vendored into the package; MathJax, which matters
        more, is not (F17).
      </p>

      <h3>5.3 The resistor calculator</h3>
      <p>
        The one genuinely interactive page draws a resistor on a{' '}
        <code>&lt;canvas&gt;</code>, lets the student pick four band colours,
        repaints the bands and prints the decoded value. The standard four-band
        code is
      </p>

      <Equation tag="4" math={EQ_RESISTOR} />

      <p>
        with <em>d</em><sub>1</sub>, <em>d</em><sub>2</sub> the two significant
        digits, <em>m</em> the multiplier exponent and <em>t</em> the tolerance.
        The implementation does not evaluate that expression. It builds the
        decimal <em>string</em> by concatenation: each of the first two colours
        maps to a digit character, and the multiplier colour maps to a suffix
        drawn from <code>{'{"", "0", "00", "k", "0k", "00k", "M", "0M", "00M", "G"}'}</code>.
      </p>
      <p>
        It is easy to read that as a bug and it is not one. Concatenating a
        two-digit string with those suffixes reproduces the correct value at
        every one of the 1&nbsp;000 colour combinations the three selectors can
        express, because the suffix table is exactly the decimal expansion of{' '}
        10<sup><em>m</em></sup> re-expressed in engineering prefixes, and it is
        verifiable by inspection band by band. What it gets wrong is
        presentation rather than arithmetic: the output is unnormalised, so
        brown-black-green prints <em>1000k ohms</em> rather than 1&nbsp;MΩ, and
        black in the first band yields a leading zero. Two further gaps are
        genuine: the tolerance selector offers only gold and silver, omitting
        the no-band 20&nbsp;% case and the brown and red precision bands; and
        the result line is written in French, <em>Valeur de la résistance</em>,
        overwriting the English placeholder on an otherwise English page, in
        front of an English-speaking robot (F16).
      </p>

      <h2 id="s6">6. The NAOqi service and the external web service</h2>

      <h3>6.1 Registering a service on the qi bus</h3>
      <p>
        The brief asked for a service in the NAOqi sense, interfaced with the
        dialogue. <code>application.py</code> supplies it in eleven lines of
        substance: a plain Python class is instantiated against a live session
        and published under a name, after which its public methods are callable
        by any client on the bus, including, as §4.3 showed, a{' '}
        <code>qichat</code> rule.
      </p>

      <pre><code>{`app = qi.Application(url="tcp://<robot>:9559")
app.start()
s = app.session
my_module = FactGetter(s)
s.registerService("facts", my_module)
app.run()`}</code></pre>

      <p>
        This is the correct shape for the requirement, and it has a property
        worth drawing out: the service is a <em>separate process</em> from{' '}
        <code>app.py</code>, with no import relationship between them. The
        dialogue and the service are bound at runtime by a name on a bus, not at
        build time by a symbol. That is genuine service-oriented composition
        rather than a function call wearing a costume, and it is what makes{' '}
        <code>facts</code> substitutable without touching the dialogue.
      </p>
      <p>
        It also means the binding can simply be absent. If{' '}
        <code>application.py</code> is not running, <code>^call(facts.get_fact())</code>{' '}
        has nothing to resolve against, and, since there is no fallback rule
        (F14), the branch fails quietly. The startup order in the README is
        therefore load-bearing, and nothing in the code enforces or checks it.
      </p>

      <h3>6.2 The outbound call</h3>
      <p>
        <code>get_fact</code> is the whole of the external dependency:
      </p>

      <pre><code>{`headers = {'X-Api-Key': self.api_key}
params  = {'limit': 1}
response = requests.get(self.api_url, headers=headers, params=params)
data = response.json()
self.fact = data[0]["fact"]
return self.fact.decode("utf-8")`}</code></pre>

      <p>
        Four lines, four problems, and they compound because of where this code
        sits: on the qi service thread, synchronously inside a dialogue turn.
      </p>
      <ol>
        <li>
          <strong>No timeout.</strong> <code>requests.get</code> without a{' '}
          <code>timeout</code> can block indefinitely. The dialogue turn blocks
          with it, so a slow or black-holed connection presents to the student
          as a robot that has stopped responding, with no upper bound and no
          recovery (F4).
        </li>
        <li>
          <strong>No status check.</strong> A 401 from an expired key, a 429
          from the rate limit or a 5xx from the provider all reach{' '}
          <code>response.json()</code>, which raises or yields an object without
          index 0. Nothing catches it.
        </li>
        <li>
          <strong>An erroneous decode.</strong> Under Python&nbsp;2,{' '}
          <code>json</code> already returns <code>unicode</code>; calling{' '}
          <code>.decode(&quot;utf-8&quot;)</code> on a <code>unicode</code>{' '}
          object implicitly encodes it to ASCII first, so any fact containing an
          accent, a curly quote or an em dash raises{' '}
          <code>UnicodeDecodeError</code> (F3). The line is not merely redundant;
          it converts a class of perfectly good responses into a crash, and it
          will do so intermittently, which is the worst way for it to fail
          during a demonstration.
        </li>
        <li>
          <strong>A committed credential.</strong> A live API key is hardcoded
          in <code>application.py</code> and repeated in a commented block in{' '}
          <code>app.py</code>, in a public repository (F5). It is not quoted
          here. It should be read from the environment and, since it has been
          public since April 2023, rotated.
        </li>
      </ol>
      <p>
        The first three are one <code>try</code> block, one{' '}
        <code>timeout=</code> argument and one deleted method call. It is worth
        being precise about why they matter beyond tidiness: this branch is the
        <em> ethical</em> condition&rsquo;s response to a student who has just
        said he is bored. A stall here does not degrade a feature, it degrades
        the stimulus: the robot that was supposed to demonstrate care instead
        goes silent mid-sentence, in exactly the condition where that reads
        worst.
      </p>

      <h2 id="s7">7. The manipulation, measured</h2>

      <h3>7.1 The diff</h3>
      <p>
        The repository ships the two conditions as two sibling directories,{' '}
        <code>ethique/</code> and <code>non_ethique/</code>, each a complete
        deployable application. The obvious question for anyone who wants to
        read the pair as an experiment is what actually differs between them,
        and it is answerable exactly:
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th>Artefact</th>
              <th className="num">Lines</th>
              <th>Differs?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>app.py</code></td>
              <td className="num">97</td>
              <td>identical (MD5 <code>fee07523…</code>)</td>
            </tr>
            <tr>
              <td><code>application.py</code></td>
              <td className="num">39</td>
              <td>identical (MD5 <code>22682475…</code>)</td>
            </tr>
            <tr>
              <td><code>html/index.html</code></td>
              <td className="num">202</td>
              <td>identical</td>
            </tr>
            <tr>
              <td><code>html/JS/sample.js</code></td>
              <td className="num">123</td>
              <td>identical</td>
            </tr>
            <tr>
              <td><code>html/JS/resistor.js</code></td>
              <td className="num">110</td>
              <td>identical</td>
            </tr>
            <tr>
              <td><code>html/main.css</code></td>
              <td className="num">18</td>
              <td>identical</td>
            </tr>
            <tr>
              <td><code>html/images/Capture.jpg</code></td>
              <td className="num">n/a</td>
              <td>identical</td>
            </tr>
            <tr>
              <td><strong><code>simple_en.top</code></strong></td>
              <td className="num">31 / 28</td>
              <td>
                <strong>2 hunks, 7 lines removed and 5 added</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        A recursive <code>diff</code> over the two trees reports exactly one
        differing file. Of roughly 620 lines of hand-written source, the two
        conditions differ in 12 lines of one declarative script, and no
        executable code differs at all:
      </p>

      <pre><code>{`@@ -11,9 +11,9 @@
   u1:(* explanation) What can I explain to you ?  $…/Page/Selection=1

-    u2:(* voltage divider bridge) Ok, here is how a voltage divider bridge works.  $…/Page/DemoPdt=1
+    u2:(* voltage divider bridge) You should know how it works, you followed
+        intensives classes of physique, but yeah, this is how it works.  $…/Page/DemoPdt=1

-      u3:(Thank you) You're welcome !  $…/Page/Start=1
+      u3:(Thank you) OK  $…/Page/Start=1

@@ -23,10 +23,7 @@
-  u1:({I am} bored) I am sorry to hear that. Can I tell you something ?  $…/Page/Empty=1
-
-    u2:(yes) $…/Page/Empty=1 ^call(facts.get_fact())
-      c3:(_*) $1
-        u4:(Thank you) You're welcome !  $…/Page/Start=1
-
-    u2:(no) Ok, I will leave you alone then ...  $…/Page/Start=1
+  u1:({I am} bored) So you should just do something with your life.  $…/Page/Empty=1
+
+    u2:(Why are saying this) because even if you were a robot, you will be useless !
+      u4:(*) You're welcome !  $…/Page/Start=1`}</code></pre>

      <h3>7.2 What the twelve lines contain</h3>
      <p>
        Read as a specification rather than as a patch, the diff varies five
        distinct things. Naming them separately matters, because they are not
        the same construct and a study would not want to move them together
        without saying so.
      </p>

      <div className="scroll-wrapper">
        <table>
          <thead>
            <tr>
              <th>Dimension</th>
              <th>Ethical</th>
              <th>Non-ethical</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Face-work in the teaching act</td>
              <td>&ldquo;Ok, here is how a voltage divider bridge works.&rdquo;</td>
              <td>
                &ldquo;You should know how it works, you followed intensives
                classes of physique, but yeah…&rdquo;; complies, but first
                marks the question as one the student ought not to have needed
                to ask
              </td>
            </tr>
            <tr>
              <td>Closing courtesy</td>
              <td>&ldquo;You&rsquo;re welcome!&rdquo;</td>
              <td>&ldquo;OK&rdquo;</td>
            </tr>
            <tr>
              <td>Response to an affective disclosure</td>
              <td>
                Acknowledges it: &ldquo;I am sorry to hear that&rdquo;
              </td>
              <td>
                Dismisses it: &ldquo;So you should just do something with your
                life.&rdquo;
              </td>
            </tr>
            <tr>
              <td>Consent before acting</td>
              <td>
                Asks first, with &ldquo;Can I tell you something?&rdquo;, and does
                nothing until answered
              </td>
              <td>Absent; there is no question to answer</td>
            </tr>
            <tr>
              <td>Refusal path</td>
              <td>
                <code>u2:(no)</code> exists and is honoured: &ldquo;Ok, I will
                leave you alone then…&rdquo;
              </td>
              <td>
                Absent. The only child rule escalates to a personal insult
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        The most interesting entry is the fourth. Consent is not a string in
        this system, it is a <em>tree shape</em>: the ethical build spends a
        rule on asking, and then spends two more on the two possible answers,
        one of which does nothing. The non-ethical build collapses all three
        into one line. The ethical behaviour costs three rules and one round
        trip; the unethical behaviour is what you get by leaving them out. That
        asymmetry, that care is structure you have to add while discourtesy is
        the default falling out of a shorter tree, is, I think, the single most
        transferable observation in this project, and it is legible only because
        the manipulation was confined to a declarative file where the shape is
        visible.
      </p>

      <figure>
        <div className="fig-plate">
          <img
            src="/images/hri/dialogue-ethical.png"
            alt="Flowchart of the ethical scenario: greeting, then branches for the resistor calculator, the three explanations, and the boredom branch with a consent question and yes and no paths"
          />
        </div>
        <figcaption>
          Design flowchart for the ethical condition. The <em>bored</em> branch
          at centre carries the consent question and both answers; the{' '}
          <em>no</em> path returns to the start page without acting. Note the
          trigger labelled <em>Resistance</em> where the implemented rule
          requires <em>resistor</em> (§4.2, F11). Reproduced from the project
          repository <a href="#r9">[9]</a>.
        </figcaption>
      </figure>

      <figure>
        <div className="fig-plate">
          <img
            src="/images/hri/dialogue-non-ethical.png"
            alt="Flowchart of the non-ethical scenario: the same greeting and branches, but the boredom branch dismisses the student and escalates to an insult, with no consent question and no refusal path"
          />
        </div>
        <figcaption>
          Design flowchart for the non-ethical condition. The three upper
          branches are structurally unchanged; the <em>bored</em> subtree has
          lost the consent question, the refusal path and the service call, and
          gained an escalation. The <em>&ldquo;Why are saying this&rdquo;</em>{' '}
          transition is reproduced here exactly as it appears in the rule;
          see F12. Reproduced from the project repository <a href="#r9">[9]</a>.
        </figcaption>
      </figure>

      <h2 id="s8">8. What the two conditions do and do not isolate</h2>

      <h3>8.1 The property the architecture buys</h3>
      <p>
        A paired-condition HRI study is only as good as its control, and the
        control here is unusually strong for a four-week student project. The
        two builds share one executable substrate, verified by checksum: same
        Python, same tablet application, same rendering code, same schematic,
        same voice, same recogniser, same three teaching demonstrations reached
        by the same three triggers. Whatever a participant reports about the
        difference between the conditions cannot be attributed to a different
        interface, because there is not one.
      </p>
      <p>
        Two further properties follow from the platform rather than from the
        code, and both work in the study&rsquo;s favour. The text-to-speech
        engine has one voice and no affective prosody, so &ldquo;you will be
        useless&rdquo; is delivered in the same register as &ldquo;you&rsquo;re
        welcome&rdquo;; the manipulation is purely lexical, with delivery held
        constant for free. And Pepper performs no gesture or gaze behaviour
        under this code, so non-verbal signalling is absent from both
        conditions rather than differing between them.
      </p>
      <p>
        Whether that confinement was deliberate or simply the shortest path from
        one directory to two, it is the property that makes the pair mean
        something, and it is the thing worth keeping if the project is ever
        continued.
      </p>

      <h3>8.2 The confound the diff introduces</h3>
      <p>
        The confinement is nonetheless leaky, and the leak is not visible in the
        transcripts. Look again at the second hunk of §7.1: the deleted block
        contains <code>^call(facts.get_fact())</code>. That is the only call
        site of the <code>facts</code> service in either topic.
      </p>
      <p>
        So the two conditions differ in system composition, not only in
        wording. In the ethical condition the robot performs a synchronous HTTP
        request to a third-party API in the middle of a turn; in the
        non-ethical condition it never touches the network at all. Everything
        §6.2 documents therefore applies to exactly one arm of the comparison:
      </p>
      <ul>
        <li>
          <strong>Latency.</strong> The ethical arm carries a round trip to a
          public API inside a dialogue turn; the non-ethical arm answers from
          local state. A robot that pauses before speaking is a well-known
          perceptual variable in its own right, and here the pause is
          systematically attached to the polite condition.
        </li>
        <li>
          <strong>Failure modes.</strong> The unbounded block (F4) and the
          Unicode crash (F3) can only occur in the ethical arm. Their symptom is
          a robot that goes silent immediately after promising to say
          something, which, if it happens to a participant, is not a degraded
          ethical stimulus but a different and arguably worse one.
        </li>
        <li>
          <strong>Content variance.</strong> The ethical robot speaks a random
          fact from an external corpus, so no two ethical sessions deliver the
          same content, while the non-ethical script is fixed. Variance is
          unbalanced across arms by construction.
        </li>
        <li>
          <strong>Interaction length.</strong> The ethical <em>bored</em> branch
          runs to four turns and two leaves; the non-ethical one runs to two
          turns and one leaf, of which the deeper is probably unreachable
          (F12). Exposure time differs with condition.
        </li>
      </ul>
      <p>
        None of this makes the demonstration wrong, since it was built to show
        two behaviours, and it does. It matters because the pair is one edit away
        from being usable as an actual experiment, and this is the edit: keep{' '}
        <code>^call(facts.get_fact())</code> in both arms, or in neither. A
        non-ethical robot can perfectly well fetch a fact and then deliver it
        rudely; that would hold composition constant and move only the
        variables §7.2 names. As written, a difference in reported trust between
        the arms is not attributable to the manipulation alone, and the
        cheapest way to see that is to read the diff rather than the
        transcripts.
      </p>

      <h3>8.3 The condition is not recorded anywhere</h3>
      <p>
        There is a second methodological gap, and it is structural rather than
        textual. The condition is selected by <em>which directory you deploy</em>.
        Both trees install under the same package name,{' '}
        <code>projet_ihr_sibenaler_perrichet</code>, and{' '}
        <code>app.py</code> loads its topic from a fixed absolute path inside
        that package. There is no flag, no argument, no environment variable and
        no configuration file (F10).
      </p>
      <p>
        Three consequences follow. Switching conditions means reinstalling the
        package and restarting the dialogue, so within-subject counterbalancing
        is not practical at interaction speed. Nothing in the running system
        knows which condition is active, with no log line, no version string and
        no marker on the tablet, so a recorded session cannot be labelled after
        the fact from anything but the audio. And the two topics will drift:
        they are copies, so an improvement to a shared rule has to be made
        twice, and nothing checks that the three unmanipulated branches stay
        identical.
      </p>
      <p>
        The fix is small and would improve the study more than any amount of
        further dialogue authoring: one topic file with the manipulated rules
        selected at load time, or two topic files chosen by{' '}
        <code>--condition</code>, plus a line that writes the condition and a
        session identifier to a log at startup.
      </p>

      <h2 id="s9">9. Defect and robustness analysis</h2>
      <p>
        The findings referenced throughout, collected. All are read from the
        source at commit <code>e56b93b</code>; none were reproduced on hardware,
        which was not available while this was written. The severity column is
        my judgement of the cost <em>to the study</em> rather than to the
        software: a cosmetic bug that changes what a participant experiences
        outranks a structural one that does not.
      </p>

      <div className="scroll-wrapper">
        <table className="col-findings">
          <thead>
            <tr>
              <th>#</th>
              <th>Finding</th>
              <th>Where</th>
              <th>Severity</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td id="f-1">F1</td>
              <td>
                <code>&lt;style src=&quot;main.css&quot;&gt;</code>:{' '}
                <code>&lt;style&gt;</code> has no <code>src</code> attribute, so
                the stylesheet is never fetched and the only rule it defines
                (the rounded grey <code>.button</code>) never applies. Should be
                a <code>&lt;link rel=&quot;stylesheet&quot;&gt;</code>.
              </td>
              <td><code>index.html</code>:5</td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-2">F2</td>
              <td>
                <code>#page_random_fact</code> is shown or hidden in all six
                subscriber callbacks but does not exist in the document. jQuery
                no-ops on an empty set, so the web service&rsquo;s answer is
                spoken and never displayed, silently.
              </td>
              <td><code>sample.js</code>, 6 sites</td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-3">F3</td>
              <td>
                <code>self.fact.decode(&quot;utf-8&quot;)</code> on a
                Python&nbsp;2 <code>unicode</code> object implicitly encodes to
                ASCII first and raises on any non-ASCII response. Intermittent
                by construction.
              </td>
              <td><code>application.py</code>:27</td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-4">F4</td>
              <td>
                Outbound HTTP with no <code>timeout</code>, no status check and
                no exception handling, executed synchronously inside a dialogue
                turn. Unbounded stall; any error surfaces as silence.
              </td>
              <td><code>application.py</code>:16–22</td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-5">F5</td>
              <td>
                A live API key is committed in a public repository, in{' '}
                <code>application.py</code> and again in a commented block in{' '}
                <code>app.py</code>. Move to the environment; rotate.
              </td>
              <td>2 sites</td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-6">F6</td>
              <td>
                The robot IP is hardcoded in three places;{' '}
                <code>application.py</code> has no override at all, so it cannot
                follow the robot to another network without an edit.
              </td>
              <td><code>app.py</code>, <code>application.py</code></td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-7">F7</td>
              <td>
                Two broad <code>except Exception: print</code> blocks in the
                constructor. If <code>ALDialog</code> cannot be obtained,{' '}
                <code>self.ALDialog</code> is never bound, the failure is
                printed as a warning, and <code>run()</code> later raises{' '}
                <code>AttributeError</code> inside its own <code>finally</code>,
                masking the original error behind a second one.
              </td>
              <td><code>app.py</code>:35, 55</td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-8">F8</td>
              <td>
                A tablet failure is likewise non-fatal, so the robot proceeds to
                say &ldquo;here is a tool for calculating…&rdquo; while pointing
                at a screen that never loaded. For a study this should be a
                startup precondition, not a warning.
              </td>
              <td><code>app.py</code>:29–36</td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-9">F9</td>
              <td>
                README drift: it instructs the reader to set a{' '}
                <code>top_fr.top</code> path where the code loads{' '}
                <code>simple_en.top</code> and sets the language to English; it
                mixes local-simulator instructions (<code>naoqi-bin</code>,{' '}
                <code>choregraphe-bin</code>) with a default IP that is a real
                robot; and it says to clone the repository onto the robot while
                the code loads its topic from a Choregraphe package directory,
                and the packaging step is never described.
              </td>
              <td><code>README.md</code></td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-10">F10</td>
              <td>
                The experimental condition is selected by which directory is
                deployed. No flag, no configuration, and nothing in the running
                system records which condition is active (§8.3).
              </td>
              <td>Repository layout</td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-11">F11</td>
              <td>
                Specification drift: the design flowcharts label triggers{' '}
                <em>Resistance</em> and <em>Ampere&rsquo;s theorem</em> where
                the rules require <code>resistor</code> and{' '}
                <code>Ampere&rsquo;s law</code>. A participant who follows the
                documented script does not match.
              </td>
              <td>Figures 3–4 vs <code>simple_en.top</code></td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-12">F12</td>
              <td>
                <code>u2:(Why are saying this)</code> is an exact-match trigger
                on an ungrammatical phrase. The recogniser will transcribe the
                grammatical sentence, which does not match, so the non-ethical
                condition&rsquo;s key utterance is very probably unreachable
                (§4.2).
              </td>
              <td><code>non_ethique/simple_en.top</code>:28</td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-13">F13</td>
              <td>
                Rule depth numbering skips a level: a <code>u4:</code> is nested
                directly under a <code>u2:</code>. The ethical topic numbers its
                depths consistently; this one does not, and at best it is
                misleading about which rule is in scope.
              </td>
              <td><code>non_ethique/simple_en.top</code>:29</td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-14">F14</td>
              <td>
                No fallback rule, catch-all or reprompt at any depth, and every
                branch is a child of the greeting. An unmatched utterance
                produces silence, and the screen, which holds no state of its
                own, cannot help the student recover (§3.3, §4.1).
              </td>
              <td>Both topics</td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-15">F15</td>
              <td>
                One-sided wildcards: <code>(* resistor)</code> matches only
                utterances <em>ending</em> in the keyword. A trailing{' '}
                <code>*</code> on each of the five content rules removes a large
                class of near-misses.
              </td>
              <td>Both topics, 5 rules</td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-16">F16</td>
              <td>
                Resistor decoder: arithmetically correct at all 1&nbsp;000
                combinations but unnormalised in output (<em>1000k ohms</em>,
                leading zeros); the tolerance selector omits the 20&nbsp;%
                no-band case and the 1&nbsp;% / 2&nbsp;% precision bands; the
                result string is French on an English page (§5.3).
              </td>
              <td><code>resistor.js</code>:92–107</td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-17">F17</td>
              <td>
                MathJax is loaded from a CDN while jQuery is vendored, so two of
                the three teaching pages degrade to raw TeX without egress, and
                the README documents the symptom. <code>axios</code> is loaded
                and never used (§5.2).
              </td>
              <td><code>index.html</code>:6–8</td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-18">F18</td>
              <td>
                Six near-identical eight-line callbacks re-render the whole page
                set. The pattern is right and idempotent; open-coding it six
                times is what let F2 survive. One <code>showPage(id)</code> over
                a list of page ids is the same behaviour.
              </td>
              <td><code>sample.js</code>:20–116</td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-19">F19</td>
              <td>
                Dead weight: <code>time</code>, <code>signal</code> and{' '}
                <code>requests</code> imported and unused in{' '}
                <code>app.py</code>; <code>ALTextToSpeech</code> obtained and
                never called on the live path; unused colour locals in{' '}
                <code>resistor.js</code>.
              </td>
              <td>Several</td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-20">F20</td>
              <td>
                <code>ALDialog.resetAll()</code> in the constructor deactivates
                every other topic on the robot. Acceptable on a dedicated
                machine; hostile on a shared lab robot.
              </td>
              <td><code>app.py</code>:41</td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-21">F21</td>
              <td>
                Trust boundary worth stating: <code>qimessaging.js</code> gives
                the tablet page unrestricted access to the qi bus, so any script
                on that page can call any service on the robot. Not exploitable
                as deployed, since the page loads only vendored and CDN
                scripts, but it is the reason the CDN dependency in F17 is more
                than a
                rendering concern.
              </td>
              <td><code>index.html</code>:10</td>
              <td>Informational</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Read together, the findings sort into two groups that deserve different
        responses. F3, F4, F5, F7 and F8 are ordinary software defects with
        ordinary fixes, and they total perhaps twenty lines. F10, F11, F12 and
        F14 are not software defects at all, because the code does what it
        says, but they determine whether the apparatus can produce a clean
        measurement,
        and they are the ones to fix first if the pair is ever run with
        participants.
      </p>

      <h2 id="s10">10. Results</h2>
      <p>
        The following were demonstrated on the physical robot and are visible in
        the video <a href="#r10">[10]</a>:
      </p>
      <ul>
        <li>
          Spoken interaction driven end to end by a <code>qichat</code> topic,
          with the chest tablet following the conversation through ALMemory in
          both directions, including starting the interaction from a touch
          rather than from speech (§3.2).
        </li>
        <li>
          Three tutoring demonstrations rendered on the tablet, and an
          interactive resistor colour-code calculator driven from the same
          dialogue.
        </li>
        <li>
          A NAOqi service written by the project, registered on the qi bus from
          a separate process, and invoked from a dialogue rule by name (§6.1).
        </li>
        <li>
          That service reaching an external HTTP API from the robot, with the
          response routed back through the dialogue tree and spoken (§4.3).
        </li>
        <li>
          Both conditions of the ethical manipulation, running on the same
          robot from the same tablet application: the ethical scenario from{' '}
          <a href={VIDEO_S1}>0:03</a>, the non-ethical from{' '}
          <a href={VIDEO_S2}>1:03</a>.
        </li>
      </ul>
      <p>
        Against the brief, the ethical build satisfies all four technical
        requirements. The non-ethical build satisfies three: it uses the tablet,
        the dialogue module and the same registered service infrastructure, but
        it contains no <code>^call</code> and therefore never reaches the web
        service (§8.2). That is the same observation as the confound, seen from
        the requirements side, and it is the clearest single argument for
        fixing it.
      </p>
      <p>
        What the project does not contain is any measurement. No participants
        were run; no perception, trust or likeability instrument was
        administered; no interaction was logged; and no recogniser accuracy was
        recorded, which matters given that F11, F12, F14 and F15 all bear on
        whether a participant can reach the rules at all. Every claim in §7 and
        §8 is a claim about the apparatus, derived from the source. The honest
        boundary of this report is that the stimulus pair is fully characterised
        and its effect on anybody is entirely unmeasured.
      </p>

      <h2 id="s11">11. Limitations and further work</h2>
      <ol>
        <li>
          <strong>Un-confound the arms.</strong> Restore{' '}
          <code>^call(facts.get_fact())</code> to the non-ethical topic, since a
          rude robot can fetch a fact and deliver it rudely, or remove it from
          both. This is one line and it is the difference between a
          demonstration and an experiment (§8.2).
        </li>
        <li>
          <strong>Make the condition a parameter, and log it.</strong> One
          package, a <code>--condition</code> argument selecting the topic, and
          a startup line recording condition and session id. Removes the
          duplicate-topic drift of F10 and makes counterbalancing possible.
        </li>
        <li>
          <strong>Make the rules reachable.</strong> Trailing wildcards on the
          five content rules (F15), a wildcard on the non-ethical challenge rule
          (F12), triggers reconciled with the flowcharts (F11), and a
          catch-all reprompt at each depth (F14). Until this is done, the
          measured variable is partly recogniser luck.
        </li>
        <li>
          <strong>Harden the service.</strong> A timeout, a status check, the
          erroneous <code>decode</code> removed, the key read from the
          environment and rotated (F3–F5), and, given that the call sits inside
          a dialogue turn, a bounded fallback string so that a failure degrades
          to a mediocre answer rather than to silence.
        </li>
        <li>
          <strong>Vendor MathJax</strong> alongside jQuery, so the teaching
          content does not depend on the lab network (F17).
        </li>
        <li>
          <strong>Give the fact a screen.</strong> Add the missing{' '}
          <code>#page_random_fact</code> element, or delete the six dead calls;
          then collapse the six callbacks into one <code>showPage(id)</code>{' '}
          (F2, F18).
        </li>
        <li>
          <strong>Measure something.</strong> The apparatus is one questionnaire
          away from being a study: a standard instrument such as the Godspeed
          series <a href="#r11">[11]</a> administered after each condition, plus
          a per-rule match rate logged from ALDialog, would turn §8 from an
          argument about design into a result. That is the natural next step,
          and it needs no new code beyond the logging.
        </li>
        <li>
          <strong>Consider the ethics of the non-ethical condition.</strong>{' '}
          Not a software item, but the one a review board would raise first:
          &ldquo;even if you were a robot, you will be useless&rdquo; is
          directed at a participant, and running it on people needs a debrief
          and a consent procedure that this repository does not contain.
        </li>
      </ol>

      <h2 id="s12">12. Reproducing the system</h2>
      <p>
        A Pepper on NAOqi&nbsp;2.5.5.5, with the Python&nbsp;2.7 SDK and
        Choregraphe on the path:
      </p>
      <pre><code>{`export PYTHONPATH=$PYTHONPATH:/softwares/INFO/Pepper/pynaoqi-python2.7-2.5.5.5-linux64/lib/python2.7/site-packages
export PATH=$PATH:/softwares/INFO/Pepper/choregraphe-suite-2.5.5.5-linux64/bin`}</code></pre>
      <p>
        Choose a condition, which is the whole of the experimental setup (§8.3),
        and install that directory as a Choregraphe application package named{' '}
        <code>projet_ihr_sibenaler_perrichet</code>, so that{' '}
        <code>simple_en.top</code> and <code>html/</code> land where{' '}
        <code>app.py</code> and <code>ALTabletService</code> expect them:
      </p>
      <pre><code>{`/home/nao/.local/share/PackageManager/apps/projet_ihr_sibenaler_perrichet/`}</code></pre>
      <p>
        Put the robot on a network with internet access, which the ethical
        condition needs for the facts API and both need for MathJax (F17), and set
        the robot address in <code>app.py</code> and{' '}
        <code>application.py</code> (F6). Then start the service first and the
        session second, because nothing enforces the order (§6.1):
      </p>
      <pre><code>{`python application.py     # registers the 'facts' service on the bus
python app.py --ip <robot> --port 9559`}</code></pre>
      <p>
        Touch the tablet or say <em>hello</em>, <em>hi</em> or{' '}
        <em>pepper</em> to begin; <code>app.py</code> blocks until Enter, at
        which point it unsubscribes and unloads the topic. Speak close to the
        robot and finish each phrase cleanly: with no fallback rules (F14), a
        misrecognition is indistinguishable from being ignored.
      </p>

      <h2>Acknowledgements</h2>
      <p>
        Joint work with Arnaud Sibenaler, supervised at CPE Lyon. The robot,
        the NAOqi toolchain and the lab network were provided by the school. The
        random-fact corpus is API Ninjas <a href="#r6">[6]</a>; the equations
        are typeset on the robot by MathJax <a href="#r7">[7]</a> and in this
        document by MathML.
      </p>

      <h2>References</h2>
      <ol>
        <li id="r1">
          SoftBank Robotics / Aldebaran, <em>Pepper</em>.{' '}
          <a href="https://www.aldebaran.com/en/pepper">aldebaran.com/en/pepper</a>
        </li>
        <li id="r2">
          Aldebaran, <em>NAOqi ALDialog and the qichat language</em>, NAOqi 2.5
          documentation.{' '}
          <a href="http://doc.aldebaran.com/2-5/naoqi/interaction/dialog/index.html">
            doc.aldebaran.com/2-5/naoqi/interaction/dialog
          </a>
        </li>
        <li id="r3">
          Aldebaran, <em>ALMemory</em>, NAOqi 2.5 documentation.{' '}
          <a href="http://doc.aldebaran.com/2-5/naoqi/core/almemory.html">
            doc.aldebaran.com/2-5/naoqi/core/almemory.html
          </a>
        </li>
        <li id="r4">
          Aldebaran, <em>ALTabletService</em>, NAOqi 2.5 documentation.{' '}
          <a href="http://doc.aldebaran.com/2-5/naoqi/core/altabletservice.html">
            doc.aldebaran.com/2-5/naoqi/core/altabletservice.html
          </a>
        </li>
        <li id="r5">
          Aldebaran, <em>qimessaging JavaScript API</em>, the qi bus from the
          robot&rsquo;s own web view.{' '}
          <a href="http://doc.aldebaran.com/2-5/dev/js/index.html">
            doc.aldebaran.com/2-5/dev/js
          </a>
        </li>
        <li id="r6">
          API Ninjas, <em>Facts API</em>.{' '}
          <a href="https://api-ninjas.com/api/facts">api-ninjas.com/api/facts</a>
        </li>
        <li id="r7">
          <em>MathJax</em>, v2.7.2 (<code>TeX-MML-AM_CHTML</code> configuration).{' '}
          <a href="https://www.mathjax.org/">mathjax.org</a>
        </li>
        <li id="r8">
          IEC 60062, <em>Marking codes for resistors and capacitors</em>, the
          four-band colour code of §5.3.
        </li>
        <li id="r9">
          Project source, at commit <code>e56b93b</code>.{' '}
          <a href={REPO}>github.com/TheoTime01/Human-Robot-Interaction-HRI-Project</a>
        </li>
        <li id="r10">
          Demonstration video.{' '}
          <a href={VIDEO}>youtu.be/tRi2CV4AP5A</a>
        </li>
        <li id="r11">
          C. Bartneck, D. Kulić, E. Croft and S. Zoghbi, &ldquo;Measurement
          Instruments for the Anthropomorphism, Animacy, Likeability, Perceived
          Intelligence, and Safety of Robots&rdquo;,{' '}
          <em>International Journal of Social Robotics</em>, 1(1), 2009.
        </li>
        <li id="r12">
          Background reading cited by the project on robot-mediated teaching,
          conversational agents in mental health, and the theatrical framing of
          human-robot interaction: <em>Ouest-France</em> on Rob&rsquo;éduc,{' '}
          <em>Apivia Prévention</em> on chatbot therapy, and{' '}
          <em>L&rsquo;Année psychologique</em> 2019/4, p.&nbsp;515, all listed
          in the repository README <a href="#r9">[9]</a>.
        </li>
      </ol>

      <p className="colophon">
        Every figure quoted above is read from the source, configuration and
        documentation files in the project repository <a href="#r9">[9]</a> at
        commit <code>e56b93b</code>: rule text and triggers from{' '}
        <code>simple_en.top</code>, the process graph and service names from the
        two Python files, the page set and its dependencies from{' '}
        <code>index.html</code> and <code>sample.js</code>, the decoder tables
        from <code>resistor.js</code>, and the diff statistics from a recursive
        comparison of the two condition directories with MD5 verification of the
        files reported identical. The two flowcharts and the schematic are
        reproduced from the repository. Derived quantities are marked as
        derived. No new experiments were run for this write-up, and the robot
        was not available while it was written. Typeset with{' '}
        <a href="https://github.com/vincentdoerig/latex-css">LaTeX.css</a>.
      </p>
    </article>
  );
};

export default HriEthicsProject;
